import React, { useMemo, useState } from "react";
import {
    Accordion,
    Button,
    FormLayout,
    HeaderActions,
    Heading,
    Input,
    LoadingProgress,
    Modal,
    Panel,
    Switch,
    Table,
    Text,
} from "@/ui";
import { Icon } from "@/components";
import { useMutation } from "@apollo/react-hooks";
import { MUTATION_UPDATE_LISTTRACK } from "../../apollo/module-operations/app";
import { MUTATION_SYNC_JANE_PRODUCT_TO_LISTRACK } from "../../apollo/module-operations/jane";
import _ from "lodash";
import omit from "lodash/omit";
import { useForm, useMeta, useNotification, useValidator } from "@/hooks";
import {
    EVENT_TYPE_LISTTRACK,
    SUBSCRIPTION_SOURCE_TYPE,
} from "config/constant";
import ListTrackAddItem from "./ListTrackAddItem";
import { PlusIcon, RowActionsMenu, storeTableUiStyles } from "./shared";
import styles from "./GeneralsForm.module.scss";
import customStyles from "./CustomizationsForm.module.scss";
import deliveryStyles from "./DeliverySchedules.module.scss";

const SaveIcon = () => <Icon icon="save" />;
const SyncIcon = () => <Icon icon="refresh" />;

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const SECTION_KEYS = [
    "order_fields",
    "password_fields",
    "store_fields",
    "messages",
    "subscribe_lists",
    "subscribe_fields",
    "sources",
];

const SECTION_TITLES = {
    order_fields: "Order Fields",
    password_fields: "Password Fields",
    store_fields: "Store Fields",
    messages: "Messages",
    subscribe_lists: "Subscribe Lists",
    subscribe_fields: "Subscribe Fields",
    sources: "Sources",
};

const SECTION_FORMS = {
    order_fields: "Order Field",
    password_fields: "Password Field",
    store_fields: "Store Field",
    messages: "Message",
    subscribe_lists: "Subscribe Lists",
    subscribe_fields: "Subscribe Fields",
    sources: "Sources",
};

const initialState = {
    form: "",
    idDelete: null,
    selected: null,
    updateList: [],
};

const ListTrackForm = ({ app, stores, loading: pageLoading }) => {
    const notify = useNotification();
    const { user } = useMeta();
    const [state, setState] = useState(initialState);
    const [showForm, setShowForm] = useState(false);
    const [isCreate, setIsCreate] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDelete, setIsDelete] = useState(false);
    const [isloading, setIsLoading] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState({
        order_fields: true,
        password_fields: false,
        store_fields: false,
        messages: false,
        subscribe_lists: false,
        subscribe_fields: false,
        sources: false,
    });

    const { inputs, inputChange, setInputs } = useForm();

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "client_app_id",
                method: "isEmpty",
                validWhen: false,
                message: "This Client id is required.",
            },
            {
                field: "client_app_secret",
                method: "isEmpty",
                validWhen: false,
                message: "This Client secret is required.",
            },
            {
                field: "data_client_id",
                method: "isEmpty",
                validWhen: false,
                message: "This Data client id is required.",
            },
            {
                field: "data_client_secret",
                method: "isEmpty",
                validWhen: false,
                message: "This Data client secret is required.",
            },
            {
                field: "list_id",
                method: "isEmpty",
                validWhen: false,
                message: "This List id is required.",
            },
        ],
        {
            ...inputs,
        },
    );

    if (!_.get(inputs, "id", null)) {
        setInputs(app);
    }

    const [mutate] = useMutation(MUTATION_UPDATE_LISTTRACK, {
        refetchQueries: [],
        onCompleted: () => {
            setInputs({
                ...inputs,
                order_fields:
                    state.form === "Order Field"
                        ? state.updateList
                        : inputs.order_fields,
                password_fields:
                    state.form === "Password Field"
                        ? state.updateList
                        : inputs.password_fields,
                store_fields:
                    state.form === "Store Field"
                        ? state.updateList
                        : inputs.store_fields,
                messages:
                    state.form === "Message"
                        ? state.updateList
                        : inputs.messages,
                subscribe_lists:
                    state.form === "Subscribe Lists"
                        ? state.updateList
                        : inputs.subscribe_lists,
                subscribe_fields:
                    state.form === "Subscribe Fields"
                        ? state.updateList
                        : inputs.subscribe_fields,
                sources:
                    state.form === "Sources"
                        ? state.updateList
                        : inputs.sources,
            });
            handleCancel();
            setIsUpdating(false);
            setIsDelete(false);
            setShowDeleteConfirm(false);
            setIsLoading(false);
            setState(initialState);
            notify(
                `${state.form} Was ${
                    isCreate ? "Created" : !isDelete ? "Updated" : "Removed"
                } Successfully"`,
                "success",
            );
        },
        onError: () => {
            setIsUpdating(false);
            notify(
                `Error ${
                    isCreate ? "Updating" : !isDelete ? "Creating" : "Removing"
                }  The ${
                    state.form
                }, If The Error Persist Please Contact Support.`,
                "error",
            );
        },
    });

    const handleDelete = list => {
        let newList = _.filter(list, item =>
            state.form === "Message"
                ? item.message_id !== state.idDelete
                : item.id !== state.idDelete,
        );
        setState({ ...state, updateList: newList });
        return newList;
    };

    const confirmDelete = () => {
        let _inputs = {
            ...inputs,
            client_id: inputs.client_app_id,
            client_secret: inputs.client_app_secret,
            order_fields:
                state.form === "Order Field"
                    ? handleDelete(inputs.order_fields)
                    : inputs.order_fields,
            password_fields:
                state.form === "Password Field"
                    ? handleDelete(inputs.password_fields)
                    : inputs.password_fields,
            store_fields:
                state.form === "Store Field"
                    ? handleDelete(inputs.store_fields)
                    : inputs.store_fields,
            messages:
                state.form === "Message"
                    ? handleDelete(inputs.messages)
                    : inputs.messages,
            subscribe_lists:
                state.form === "Subscribe Lists"
                    ? handleDelete(inputs.subscribe_lists)
                    : inputs.subscribe_lists,
            subscribe_fields:
                state.form === "Subscribe Fields"
                    ? handleDelete(inputs.subscribe_fields)
                    : inputs.subscribe_fields,
            sources:
                state.form === "Sources"
                    ? handleDelete(inputs.sources)
                    : inputs.sources,
        };

        setIsUpdating(true);
        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: {
                    ...omit(_inputs, ["client_app_id", "client_app_secret"]),
                },
            },
        });
    };

    const openEdit = (context, rowIndex) => {
        setIsCreate(false);
        setState({ ...state, form: context, selected: rowIndex });
        setShowForm(true);
        window.scrollTo(0, 0);
    };

    const openDelete = (context, row) => {
        let id = context === "Message" ? row.message_id : row.id;
        setState({ ...state, form: context, idDelete: id });
        setIsDelete(true);
        setIsCreate(false);
        setShowDeleteConfirm(true);
    };

    const renderFormCreate = form => {
        setState({
            ...state,
            form: form,
        });
        setIsCreate(true);
        setShowForm(true);
        window.scrollTo(0, 0);
    };

    const handlerUpdatingOrCreate = newArray => {
        let _inputs = {
            ...inputs,
            client_id: inputs.client_app_id,
            client_secret: inputs.client_app_secret,
            order_fields:
                state.form === "Order Field" ? newArray : inputs.order_fields,
            password_fields:
                state.form === "Password Field"
                    ? newArray
                    : inputs.password_fields,
            store_fields:
                state.form === "Store Field" ? newArray : inputs.store_fields,
            messages: state.form === "Message" ? newArray : inputs.messages,
            subscribe_lists:
                state.form === "Subscribe Lists"
                    ? newArray
                    : inputs.subscribe_lists,
            subscribe_fields:
                state.form === "Subscribe Fields"
                    ? newArray
                    : inputs.subscribe_fields,
            sources: state.form === "Sources" ? newArray : inputs.sources,
        };

        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: {
                    ...omit(_inputs, ["client_app_id", "client_app_secret"]),
                },
            },
        });
    };

    const handleCancel = () => {
        setIsUpdating(false);
        setShowForm(false);
    };

    const createOrUpdate = () => {
        if (isSubmited) {
            isSubmited(true);
        }

        if (isValid()) {
            setIsLoading(true);
            handlerUpdatingOrCreate();
        }
    };

    const [mutateSyncProductsFromTreez, { loading: syncLoading }] = useMutation(
        MUTATION_SYNC_JANE_PRODUCT_TO_LISTRACK,
        {
            onCompleted: () => {
                notify("Sync Products From Jane Successfully", "success");
            },
            onError: () => {
                notify("Error Trying to Sync Product", "error");
            },
        },
    );

    const syncProducstFromJane = async id => {
        mutateSyncProductsFromTreez({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                jane_store_id: id,
            },
        });
    };

    const openSections = SECTION_KEYS.filter(key => isCollapsed[key]);

    const handleAccordionChange = values => {
        setIsCollapsed(prev => ({
            ...prev,
            ...Object.fromEntries(
                SECTION_KEYS.map(key => [key, values.includes(key)]),
            ),
        }));
    };

    const _orderFields = (inputs && inputs.order_fields) || [];
    const _passwordFields = (inputs && inputs.password_fields) || [];
    const _storeFields = (inputs && inputs.store_fields) || [];
    const _messages = (inputs && inputs.messages) || [];
    const _subscribeLists = (inputs && inputs.subscribe_lists) || [];
    const _subscribeFields = (inputs && inputs.subscribe_fields) || [];
    const _sourcesFields = (inputs && inputs.sources) || [];

    const sectionData = {
        order_fields: _orderFields,
        password_fields: _passwordFields,
        store_fields: _storeFields,
        messages: _messages,
        subscribe_lists: _subscribeLists,
        subscribe_fields: _subscribeFields,
        sources: _sourcesFields,
    };

    const defaultColumns = useMemo(
        () => [
            {
                id: "actions",
                header: "",
                width: 52,
                align: "center",
                renderCell: row => (
                    <span
                        className={storeTableUiStyles.rowActions}
                        onClick={event => event.stopPropagation()}
                    >
                        <RowActionsMenu
                            disabled={isUpdating || showForm}
                            label={String(row.id ?? row._index + 1)}
                            onDelete={() => openDelete(row._context, row)}
                            onUpdate={() => openEdit(row._context, row._index)}
                        />
                    </span>
                ),
            },
            {
                id: "id",
                header: "Id",
                minWidth: 180,
                renderCell: row => `${row.id || ""}`,
            },
            {
                id: "name",
                header: "Name",
                minWidth: 180,
                renderCell: row => `${row.name || ""}`,
            },
        ],
        [isUpdating, showForm, state],
    );

    const messageColumns = useMemo(
        () => [
            {
                id: "actions",
                header: "",
                width: 52,
                align: "center",
                renderCell: row => (
                    <span
                        className={storeTableUiStyles.rowActions}
                        onClick={event => event.stopPropagation()}
                    >
                        <RowActionsMenu
                            disabled={isUpdating || showForm}
                            label={String(row.message_id ?? row._index + 1)}
                            onDelete={() => openDelete(row._context, row)}
                            onUpdate={() => openEdit(row._context, row._index)}
                        />
                    </span>
                ),
            },
            {
                id: "message_id",
                header: "Message Id",
                minWidth: 180,
                renderCell: row => `${row.message_id || ""}`,
            },
            {
                id: "event_type",
                header: "Event Type",
                minWidth: 180,
                renderCell: row =>
                    EVENT_TYPE_LISTTRACK?.find(e => e.value === row?.event_type)
                        ?.label || "",
            },
        ],
        [isUpdating, showForm, state],
    );

    const sourceColumns = useMemo(
        () => [
            {
                id: "actions",
                header: "",
                width: 52,
                align: "center",
                renderCell: row => (
                    <span
                        className={storeTableUiStyles.rowActions}
                        onClick={event => event.stopPropagation()}
                    >
                        <RowActionsMenu
                            disabled={isUpdating || showForm}
                            label={String(row.id ?? row._index + 1)}
                            onDelete={() => openDelete(row._context, row)}
                            onUpdate={() => openEdit(row._context, row._index)}
                        />
                    </span>
                ),
            },
            {
                id: "id",
                header: "Id",
                minWidth: 180,
                renderCell: row => `${row.id || ""}`,
            },
            {
                id: "type",
                header: "type",
                minWidth: 180,
                renderCell: row =>
                    SUBSCRIPTION_SOURCE_TYPE?.find(e => e.value === row?.type)
                        ?.label || "",
            },
            {
                id: "event_id",
                header: "Event Id",
                minWidth: 180,
                renderCell: row => `${row.event_id || ""}`,
            },
        ],
        [isUpdating, showForm, state],
    );

    const columnsBySection = {
        order_fields: defaultColumns,
        password_fields: defaultColumns,
        store_fields: defaultColumns,
        messages: messageColumns,
        subscribe_lists: defaultColumns,
        subscribe_fields: defaultColumns,
        sources: sourceColumns,
    };

    const syncMenuItems =
        stores?.length > 0
            ? stores.map(item => ({
                  id: String(item.value),
                  label: item.label,
                  onSelect: () => syncProducstFromJane(item.value),
              }))
            : [
                  {
                      id: "empty",
                      label: "Not data ...",
                      disabled: true,
                  },
              ];

    const addItemMenuItems = [
        {
            id: "order-field",
            label: "Order Field",
            onSelect: () => renderFormCreate("Order Field"),
        },
        {
            id: "password-field",
            label: "Password Field",
            onSelect: () => renderFormCreate("Password Field"),
        },
        {
            id: "store-field",
            label: "Store Field",
            onSelect: () => renderFormCreate("Store Field"),
        },
        {
            id: "message",
            label: "Message",
            onSelect: () => renderFormCreate("Message"),
        },
        {
            id: "subscribe-lists",
            label: "Subscribe Lists",
            onSelect: () => renderFormCreate("Subscribe Lists"),
        },
        {
            id: "subscribe-fields",
            label: "Subscribe Fields",
            onSelect: () => renderFormCreate("Subscribe Fields"),
        },
        {
            id: "sources",
            label: "Sources",
            onSelect: () => renderFormCreate("Sources"),
        },
    ];

    return (
        <>
            <Modal
                description={`Are you sure you want to remove the item from ${state.form} ?`}
                footer={
                    <div className={deliveryStyles.modalActions}>
                        <Button
                            loading={isUpdating}
                            onClick={confirmDelete}
                            type="button"
                        >
                            Confirm
                        </Button>
                        <Button
                            disabled={isUpdating}
                            onClick={() => {
                                setShowDeleteConfirm(false);
                                setIsDelete(false);
                            }}
                            type="button"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </div>
                }
                onOpenChange={open => {
                    if (!open) {
                        setShowDeleteConfirm(false);
                        setIsDelete(false);
                    }
                }}
                open={showDeleteConfirm}
                title="Remove item"
            >
                {null}
            </Modal>

            <Panel
                className={styles.root}
                header={
                    <div className={styles.headerBar}>
                        <div className={styles.headerText}>
                            <Heading level={4}>ListTrak</Heading>
                            <Text tone="secondary" variant="body-sm">
                                ListTrak Information
                            </Text>
                        </div>
                        <HeaderActions
                            actions={[
                                {
                                    id: "sync-products",
                                    label: "Sync Products",
                                    icon: syncLoading ? undefined : (
                                        <SyncIcon />
                                    ),
                                    loading: syncLoading,
                                    disabled: syncLoading,
                                    variant: "secondary",
                                    menuItems: syncMenuItems,
                                },
                                {
                                    id: "add-item",
                                    label: "Add Item",
                                    icon: <PlusIcon />,
                                    variant: "secondary",
                                    disabled: isUpdating || showForm,
                                    menuItems: addItemMenuItems,
                                },
                                {
                                    id: "update",
                                    label: "Update",
                                    icon: isloading ? undefined : <SaveIcon />,
                                    loading: isloading,
                                    disabled: isloading || showForm,
                                    onClick: createOrUpdate,
                                },
                            ]}
                            aria-label="ListTrak actions"
                        />
                    </div>
                }
                showHeaderDivider
            >
                {user.loading || pageLoading ? (
                    <div className={styles.loading}>
                        <LoadingProgress
                            indeterminate
                            indicator="circular"
                            size="sm"
                            title="Loading"
                        />
                    </div>
                ) : (
                    <FormLayout>
                        <FormLayout.Section title="Information">
                            <Switch
                                checked={Boolean(inputs.used_for_subscription)}
                                label="Used for subscription"
                                name="used_for_subscription"
                                onChange={inputChange}
                            />
                            <FormLayout.Grid columns={2} maxWidth="twoThirds">
                                <Input
                                    error={fieldError(
                                        validator,
                                        "client_app_id",
                                    )}
                                    fullWidth
                                    label="Client Id"
                                    name="client_app_id"
                                    onChange={inputChange}
                                    placeholder="Client Id"
                                    value={inputs.client_app_id || ""}
                                />
                                <Input
                                    error={fieldError(
                                        validator,
                                        "client_app_secret",
                                    )}
                                    fullWidth
                                    label="Client Secret"
                                    name="client_app_secret"
                                    onChange={inputChange}
                                    placeholder="Client Secret"
                                    value={inputs.client_app_secret || ""}
                                />
                                <Input
                                    error={fieldError(
                                        validator,
                                        "data_client_id",
                                    )}
                                    fullWidth
                                    label="Data Client Id"
                                    name="data_client_id"
                                    onChange={inputChange}
                                    placeholder="Data Client Id"
                                    value={inputs.data_client_id || ""}
                                />
                                <Input
                                    error={fieldError(
                                        validator,
                                        "data_client_secret",
                                    )}
                                    fullWidth
                                    label="Data Client Secret"
                                    name="data_client_secret"
                                    onChange={inputChange}
                                    placeholder="Data Client Secret"
                                    value={inputs.data_client_secret || ""}
                                />
                                <Input
                                    error={fieldError(validator, "list_id")}
                                    fullWidth
                                    label="List Id"
                                    name="list_id"
                                    onChange={inputChange}
                                    placeholder="List Id"
                                    value={inputs.list_id || ""}
                                />
                            </FormLayout.Grid>
                        </FormLayout.Section>

                        {showForm ? (
                            <ListTrackAddItem
                                app={inputs}
                                handleCancel={handleCancel}
                                handlerUpdatingOrCreate={
                                    handlerUpdatingOrCreate
                                }
                                isCreate={isCreate}
                                isUpdating={isUpdating}
                                setState={setState}
                                state={state}
                            />
                        ) : null}

                        <Accordion
                            className={customStyles.accordion}
                            onValueChange={handleAccordionChange}
                            type="multiple"
                            value={openSections}
                            variant="expandablePanel"
                        >
                            {SECTION_KEYS.map(key => {
                                const context = SECTION_FORMS[key];
                                const rows = (sectionData[key] || []).map(
                                    (item, index) => ({
                                        ...item,
                                        _index: index,
                                        _context: context,
                                    }),
                                );

                                return (
                                    <Accordion.Item key={key} value={key}>
                                        <Accordion.Header>
                                            <Accordion.Trigger>
                                                {SECTION_TITLES[key]}
                                            </Accordion.Trigger>
                                        </Accordion.Header>
                                        <Accordion.Content>
                                            <Table
                                                columns={columnsBySection[key]}
                                                getRowId={row =>
                                                    String(row._index)
                                                }
                                                rows={rows}
                                            />
                                        </Accordion.Content>
                                    </Accordion.Item>
                                );
                            })}
                        </Accordion>
                    </FormLayout>
                )}
            </Panel>
        </>
    );
};

export default ListTrackForm;
